import type { ReactNode } from "react";

import { useT } from "../i18n/catalogue.js";
import { cn } from "../lib/cn.js";

/**
 * The three states every data-backed screen needs, in one file because they are one decision.
 *
 * Before this package they were improvised per screen — `<p className="text-muted">Loading…</p>`
 * in one route, a bare sentence in another, an untyped `role="alert"` in a third. That is how a
 * product ends up telling a member "Could not load your account." on one screen and
 * "Something went wrong" on the next.
 *
 * Each takes a message rather than inventing one: what went wrong is the caller's knowledge, and a
 * generic string here would throw it away.
 */

interface StateProps {
  /** What to tell the member. Required — a state with no explanation reads as a broken screen. */
  children: ReactNode;
  className?: string;
}

/**
 * Nothing to show, and that is not an error — an empty chart, a household with no invites yet.
 *
 * Distinct from ErrorState on purpose. Conflating them tells someone their data failed to load
 * when in fact they have not added any, which is a materially different thing to be told.
 */
export function EmptyState({ children, className }: StateProps): ReactNode {
  return (
    <div className={cn("rounded-control border border-input bg-input/30 px-4 py-8", className)}>
      <p className="text-center text-sm text-muted">{children}</p>
    </div>
  );
}

/**
 * In flight.
 *
 * `role="status"` with `aria-live="polite"` so a screen reader announces the wait rather than
 * leaving someone on a silent screen, and politely so it does not interrupt whatever is being read.
 */
export function LoadingState({ children, className }: Partial<StateProps>): ReactNode {
  /*
   * THE DEFAULT IS RESOLVED IN THE BODY, NOT IN THE PARAMETER LIST — feature 021.
   *
   * `children = "Loading…"` was a literal evaluated at call time, which is fine for a constant and
   * wrong for a translation: a default argument cannot call a hook, and hoisting the lookup out of
   * the component would freeze it at module-evaluation time, in whichever language happened to be
   * loaded first.
   */
  const t = useT();

  return (
    <p className={cn("text-sm text-muted", className)} role="status" aria-live="polite">
      {children ?? t("ui.loading")}
    </p>
  );
}

/**
 * Something failed.
 *
 * `role="alert"` — assertive, because unlike a loading state this needs to interrupt: the member is
 * about to act on a screen whose contents are wrong or missing.
 */
export function ErrorState({ children, className }: StateProps): ReactNode {
  return (
    <p className={cn("text-sm text-muted", className)} role="alert">
      {children}
    </p>
  );
}
